import './ConfirmationPageStyle.less';

import { Icon, Timeline } from '@opendoor/bricks';
import moment from 'moment';
import React from 'react';

import { trackEvent } from '../../api/analytics';
import { IListingOfferBasic, IRequestStatus } from '../../declarations';
import layoutWrapper from '../../hocs/layoutWrapper';
import AccountRegistrationForm from '../account/AccountRegistrationForm';

export interface IConfirmationPageProps {
  address: string;
  addressToken: string;
  agentEmail: string;
  agentFullName: string;
  agentPhoneNumber: string;
  backupOffer: boolean;
  delayedOfferResponsePilotResponseDay: Date | null;
  homeAdvisorEmail: string;
  homeAdvisorName: string;
  inDelayedOfferResponsePilotPeriod: boolean;
  register: (
    email: string,
    name: string,
    password: string,
    phone: string,
    rememberMe: boolean,
  ) => void;
  registrationRequestStatus: IRequestStatus;
  slug: string;
  listingOffer: IListingOfferBasic;
}

const SUNDAY_IDX = 0;

export class ConfirmationPage extends React.PureComponent<IConfirmationPageProps> {
  public state = {
    callQueued: false,
    offerConfirmedDate: moment().format('MMM D, YYYY h:mma'),
    offerSubmittedDate: moment().format('MMM D, YYYY h:mma'),
  };

  public renderNextSteps = () => {
    const nextSteps = [
      {
        header: this.state.offerSubmittedDate,
        isStepCompleted: true,
        text: 'Your offer was submitted.',
      },
      {
        header: this.state.offerConfirmedDate,
        isStepCompleted: true,
        text: 'Opendoor confirmed receipt of your offer.',
      },
    ];
    nextSteps.push({
      header: this.props.inDelayedOfferResponsePilotPeriod
        ? `Estimated response time: ${moment(
            this.props.delayedOfferResponsePilotResponseDay,
          ).format('MMM D, YYYY')}`
        : `Estimated response time: On ${moment(this.props.listingOffer.expires_at).format(
            'MMM D, YYYY',
          )} by 12:00 pm`,
      isStepCompleted: false,
      text: 'You can expect to hear back from Opendoor by this time.',
    });
    return nextSteps;
  };

  public isCurrentTimeWithinBusinessHours = () => {
    const currentTime = new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' });
    const currentTimeCT = new Date(currentTime);
    const currentDay = currentTimeCT.getDay();
    const currentHours = currentTimeCT.getHours();

    if (currentDay === SUNDAY_IDX) {
      if (currentHours < 10 || currentHours > 13) {
        return false;
      }
      return true;
    }
    if (currentHours < 9 || currentHours > 17) {
      return false;
    }
    return true;
  };

  public renderCallStatus = () => {
    if (this.state.callQueued) {
      if (this.isCurrentTimeWithinBusinessHours()) {
        return <p>Please allow up to an hour for us to get back.</p>;
      } else {
        return (
          <p>Sorry, we are outside of business hours. We will call you as soon as possible.</p>
        );
      }
    }
    return <p />;
  };

  public render(): JSX.Element {
    const {
      address,
      agentEmail,
      agentFullName,
      agentPhoneNumber,
      backupOffer,
      homeAdvisorEmail,
      homeAdvisorName,
      registrationRequestStatus,
      register,
    } = this.props;

    return (
      <div className="od-confirmation-page">
        <div className="od-confirmation-page__content">
          <div className="od-confirmation-page__section">
            <h4 className="text-bolder extra-bottom-margin">
              Thank you! We’ve received your {backupOffer ? 'backup ' : ''}
              offer for {address}.
            </h4>
            <p>
              A confirmation email will be sent to <a href={`mailto:${agentEmail}`}>{agentEmail}</a>
              . If you have any questions, feel free to contact {homeAdvisorName} directly at{' '}
              <a href={`mailto:${homeAdvisorEmail}`}>{homeAdvisorEmail}</a>.
            </p>
          </div>
          <h4 className="text-bolder extra-bottom-margin extra-top-left-margin">
            Here's what's next
          </h4>
          <div className="timeline-container">
            <Timeline steps={this.renderNextSteps()} alternateSteps={false} />
          </div>
          <div className="divider" />
          <div className="divider" />
          <div className="od-confirmation-page__section">
            <div className="card-icon-container">
              <Icon
                className="od-agent-resale-confirmation-page__bonus-commission-icon"
                name="for-sale"
              />
            </div>
            <h4 className="text-bolder extra-bottom-margin">Help a client sell to Opendoor</h4>
            <p>If you have a client that needs to sell fast, we can help with that.</p>
            <p>Request an offer today.</p>
            <div className="agent-resale-offer-card-button-container">
              <a href={this.getUrl()} target="_blank">
                <button
                  className="agent-resale-offer-card-button button"
                  onClick={() => this.trackClickEvent('get-an-offer')}
                >
                  Get an offer
                </button>
              </a>
            </div>
            <div className="od-agent-resale-confirmation-page__bonus-commission-card">
              <div>
                <div className="card-subheader">Earn more with Opendoor Key Agent™</div>
                <div className="card-text-body">
                  <p>
                    When you sign up or request cash offers for your clients, you get enrolled into
                    the Key Agent program. Earn more on top of your compensation, and even bonuses
                    for multiple transactions.{' '}
                  </p>
                </div>
                <div>
                  <a
                    href="https://www.opendoor.com/agents"
                    className="key-agent-link"
                    onClick={() => this.trackClickEvent('key-agent-link')}
                    target="_blank"
                  >
                    Learn more about Opendoor Key Agent™
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="divider" />
          <div className="divider" />
          <div className="od-confirmation-page__section">
            <h4 className="text-bolder extra-bottom-margin">
              Closing is easier with Opendoor. Sign up today.
            </h4>
            <p>
              Everything you need to close is in one place. You can keep track of documents that
              need to be signed and approved, and you'll be notified when something needs your
              attention.
            </p>
            <section>
              {registrationRequestStatus.success ? (
                <p>
                  Thank you for signing up. Please feel free to contact&nbsp;
                  <a href={`mailto:${homeAdvisorEmail}`}>{homeAdvisorEmail}</a>
                  &nbsp;directly if you have any questions!
                </p>
              ) : (
                <AccountRegistrationForm
                  disableEmail={true}
                  email={agentEmail}
                  loading={registrationRequestStatus.isLoading}
                  name={agentFullName}
                  phone={agentPhoneNumber}
                  registrationError={registrationRequestStatus.error}
                  showInline={true}
                  verifyEmail={false}
                  onSubmit={register}
                />
              )}
            </section>
          </div>
        </div>
      </div>
    );
  }

  private trackClickEvent(clickEvent) {
    const source = 'confirmation';
    trackEvent('resale-offer-portal', 'click', clickEvent, { source });
  }

  private getLink(): string {
    return 'c_resaleportal_complete';
  }

  private getUrl(): string {
    return `https://www.opendoor.com/t/f_partnership/${this.getLink()}/p_agent?route=agents`;
  }
}

export default layoutWrapper<IConfirmationPageProps>(ConfirmationPage, {
  footerType: 'MINIMAL',
  headerType: 'MINIMAL',
});
